import { useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  facilityCompleteness,
  facilityHasRequiredFields,
} from "@/components/account/facility/facility-completeness";
import {
  FacilityProfileEditor,
  type FacilityData,
} from "@/components/account/facility/facility-profile-editor";
import { FacilityPublishBar } from "@/components/account/facility/facility-publish-bar";
import {
  FacilityStateCard,
  facilityErrorKind,
} from "@/components/account/facility/facility-state-card";

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/profile")({
  component: FacilityProfilePage,
});

type FacilityProfileResponse = FacilityData & {
  ratingAvg: number | string | null;
  availableLanguages: string[];
};

// Publish/unpublish is reserved for these roles — legacy
// facility-profile-page-client.tsx:16 PUBLISH_ROLES (facility_manager can edit
// the profile but not publish it). Server enforces MANAGE_ROLES_PUBLISH too.
const PUBLISH_ROLES = ["owner", "director", "admin"];

// Slim slice of the workspace-switcher payload — same endpoint + query key as
// workspace-switcher.tsx, so this is a cache hit on any authenticated page.
interface WorkspacesResponse {
  workspaces: { type: string; id: string; role: string }[];
}

function FacilityProfilePage() {
  const { facilityId } = Route.useParams();
  const { i18n } = useLingui();
  const locale = i18n.locale;

  const [facility, setFacility] = useState<FacilityData | null>(null);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(["ro", "en", "bg"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"no-access" | "load-failed" | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Caller's role in this facility — the apps/api JWT has no facilityTeams
  // claim (unlike the legacy session), so resolve it from the workspaces
  // payload the header switcher already fetches.
  const workspacesQ = useQuery({
    queryKey: ["api", "/api/account/workspaces"] as const,
    queryFn: () => apiFetch.get<WorkspacesResponse>("/api/account/workspaces"),
    retry: false,
  });
  const facilityRole = workspacesQ.data?.workspaces.find(
    (w) => w.type === "facility" && w.id === facilityId,
  )?.role;
  const canManagePublish = !!facilityRole && PUBLISH_ROLES.includes(facilityRole);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    apiFetch
      .get<FacilityProfileResponse>(`/api/account/facility/${facilityId}/profile`, {
        signal: controller.signal,
      })
      .then((data) => {
        setFacility({
          ...data,
          ratingAvg: data.ratingAvg != null ? Number(data.ratingAvg) : null,
        });
        setAvailableLanguages(data.availableLanguages ?? ["ro", "en", "bg"]);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(facilityErrorKind(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [facilityId, reloadKey]);

  // The editor reports the profile as saved on the server; the Publish bar
  // reads the same data (status stays owned by this page — the bar changes it).
  const handleEditorChange = useCallback((next: FacilityData) => {
    setFacility((prev) => (prev ? { ...next, status: prev.status } : next));
  }, []);

  // The editor must only mount once per facility, with the loaded data: keep
  // that snapshot as its `initialData` so live edits don't reset it. Keyed by
  // id — the route stays mounted when only $facilityId changes, and a stale
  // seed would edit (and PUT to) the previous facility.
  const [editorSeed, setEditorSeed] = useState<FacilityData | null>(null);
  if (!loading && facility && editorSeed?.id !== facility.id) setEditorSeed(facility);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  // Load failures first: only a 403/404 means "no access".
  if (error || !facility) {
    return (
      <FacilityStateCard
        kind={error ?? "load-failed"}
        facilityId={facilityId}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  // Same formula as the editor ring and the server's publish gate.
  const hasRequiredFields = facilityHasRequiredFields(facility);
  const completeness = facilityCompleteness(facility);
  const countryCode = facility.addressCountry || "RO";

  // canEdit: GET succeeded → user has MANAGE_ROLES_PROFILE (owner/director/facility_manager/admin).
  // canManagePublish: legacy gating — the publish bar renders only for
  // PUBLISH_ROLES (owner/director/admin); server enforces the same list.
  return (
    <div className="space-y-6">
      {canManagePublish && (
        <FacilityPublishBar
          facilityId={facilityId}
          status={facility.status}
          completeness={completeness}
          hasRequiredFields={hasRequiredFields}
          onStatusChange={(next) =>
            setFacility((prev) => (prev ? { ...prev, status: next } : prev))
          }
        />
      )}
      <FacilityProfileEditor
        key={facility.id}
        initialData={editorSeed ?? facility}
        locale={locale}
        countryCode={countryCode}
        canEdit
        availableLanguages={availableLanguages}
        onChange={handleEditorChange}
      />

      <AccountSecurityBanner />
    </div>
  );
}
